/**
 * Classic or One: which experience the staff app is in, chosen per device
 * from the account menu (docs/features/object-ui-and-skills/04_frontend_spec.md
 * §5.10). A preference, not a security gate.
 *
 * One is in force only while the `one` feature is on AND One is chosen; with
 * the feature off a stored choice is ignored, so the app is exactly Classic.
 * Several components read it at once (the account menu, the layout, the root
 * redirect), so it is an external store they all subscribe to.
 */
import { useCallback, useSyncExternalStore } from "react";

import { usePlatformCapabilities } from "../capabilities/context";
import { isFeatureEnabled } from "../config/features";

export const EXPERIENCE_STORAGE_KEY = "cis.experience";

export type Experience = "classic" | "one";

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Anything stored that is not `one` (nothing, an old value) is Classic. */
export function parseExperience(stored: string | null): Experience {
  return stored === "one" ? "one" : "classic";
}

/** The experience in force: One only while the feature is on and One is chosen. */
export function activeExperience(chosen: Experience): Experience {
  return chosen === "one" && isFeatureEnabled("one") ? "one" : "classic";
}

export function useExperience(): {
  /** What this device chose, whether or not One is offered. */
  chosen: Experience;
  /** What is in force. */
  experience: Experience;
  /** Whether the switch is offered at all. */
  isOneOffered: boolean;
  setExperience: (next: Experience) => void;
} {
  const capabilities = usePlatformCapabilities();
  const stored = useSyncExternalStore(subscribe, () =>
    capabilities.getStorageItem(EXPERIENCE_STORAGE_KEY),
  );
  const chosen = parseExperience(stored);
  const setExperience = useCallback(
    (next: Experience) => {
      capabilities.setStorageItem(
        EXPERIENCE_STORAGE_KEY,
        next === "one" ? "one" : null,
      );
      listeners.forEach((l) => l());
    },
    [capabilities],
  );
  return {
    chosen,
    experience: activeExperience(chosen),
    isOneOffered: isFeatureEnabled("one"),
    setExperience,
  };
}
